// src/evaluations/AssessmentResultModal.js
import { useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    IconButton,
    Typography,
    Box,
    CircularProgress
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import DescriptionIcon from "@mui/icons-material/Description";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { useTranslation } from 'react-i18next';
import api from '../config/api';
import useStore from '../config/store';
import AssessmentResultView from './AssessmentResultView';
// Modo simple: sin psicolaboral ni NineBox (PLAN_UNIFICACION_ASSESSMENT.md)
import AssessmentResultViewSimple from './AssessmentResultViewSimple';
import { isSimpleAssessment } from './assessmentMode';
// Informe de Assessment (PLAN_UNIFICACION_ASSESSMENT.md §7 y §7.10): reemplaza al reporte
// anterior en las respuestas procesadas desde el 2026-10-18 (ver potentialReportAccess.js)
import PotentialReportView from './potentialReport/PotentialReportView';
import { getResultModalView } from './potentialReport/potentialReportAccess';

// Descargas del Informe de Assessment: el backend genera el PDF o el Word editable al
// pedirlos, con los mismos datos que la pantalla (PLAN_UNIFICACION_ASSESSMENT.md §7.12).
const POTENTIAL_DOWNLOADS = {
    pdf: { path: 'export-pdf', type: 'application/pdf' },
    docx: {
        path: 'export-word',
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    }
};

export default function AssessmentResultModal({ open, onClose, data }) {
    const { t } = useTranslation();
    const { authToken, showAlert } = useStore.getState();

    const showEmpty = !data;
    const hasPdfUrl = data?.reportUrl;
    const hasWordUrl = data?.wordReportUrl;

    // Procesadas desde el reemplazo: solo el Informe de Assessment. Anteriores: solo
    // el reporte de siempre. Nunca se muestran los dos (ver potentialReportAccess.js).
    const { onlyPotential } = getResultModalView(data);
    // Formato que se está descargando ('pdf' | 'docx'), o null si no hay descarga en curso.
    const [downloadingPotential, setDownloadingPotential] = useState(null);

    const handleDownloadPotential = async (format) => {
        const download = POTENTIAL_DOWNLOADS[format];
        if (!data?.id || !download || downloadingPotential) return;
        setDownloadingPotential(format);
        try {
            const response = await api.get(
                `/evaluation-responses/${data.id}/potential-report/${download.path}`,
                { headers: { Authorization: `Bearer ${authToken}` }, responseType: 'blob' }
            );
            const disposition = response.headers?.['content-disposition'] || '';
            const match = disposition.match(/filename="?([^";]+)"?/i);
            const url = window.URL.createObjectURL(new Blob([response.data], { type: download.type }));
            const link = document.createElement('a');
            link.href = url;
            link.download = match ? match[1] : `informe-assessment-${data.applicantName || 'reporte'}.${format}`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error al descargar el informe del assessment:', error);
            showAlert(t('evaluationResponses.potentialReportDownloadError'), 'error');
        } finally {
            setDownloadingPotential(null);
        }
    };

    const handleDownloadPDF = async () => {
        if (!hasPdfUrl) return;

        try {
            const response = await fetch(data.reportUrl);
            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = `assessment-${data.applicantName || 'reporte'}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);
        } catch (error) {
            console.error('Error al descargar PDF:', error);
            // Fallback: abrir en nueva pestaña
            window.open(data.reportUrl, '_blank');
        }
    };

    const handleDownloadWord = async () => {
        if (!hasWordUrl) return;

        try {
            const response = await fetch(data.wordReportUrl);
            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = `assessment-${data.applicantName || 'reporte'}.docx`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);
        } catch (error) {
            console.error('Error al descargar Word:', error);
            // Fallback: abrir en nueva pestaña
            window.open(data.wordReportUrl, '_blank');
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                sx: { minHeight: '80vh', maxHeight: '90vh' }
            }}
        >
            <DialogTitle>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <Typography variant="h6">
                        {t('evaluationResponses.assessmentResults')}
                        {data?.applicantName && ` - ${data.applicantName}`}
                    </Typography>
                    <IconButton onClick={onClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent dividers sx={{ p: 0, overflow: 'auto' }}>
                {showEmpty ? (
                    <Typography variant="body2" color="text.secondary" sx={{ p: 3 }}>
                        {t('evaluationResponses.noResultsAvailable')}
                    </Typography>
                ) : onlyPotential ? (
                    <PotentialReportView responseId={data.id} />
                ) : isSimpleAssessment(data) ? (
                    <AssessmentResultViewSimple data={data} />
                ) : (
                    <AssessmentResultView data={data} />
                )}
            </DialogContent>

            <DialogActions>
                {onlyPotential ? (
                    <>
                        <Button
                            startIcon={downloadingPotential === 'pdf' ? <CircularProgress size={16} /> : <PictureAsPdfIcon />}
                            variant="outlined"
                            onClick={() => handleDownloadPotential('pdf')}
                            disabled={Boolean(downloadingPotential)}
                        >
                            {t('evaluationResponses.downloadPdf')}
                        </Button>

                        <Button
                            startIcon={downloadingPotential === 'docx' ? <CircularProgress size={16} /> : <DescriptionIcon />}
                            variant="outlined"
                            onClick={() => handleDownloadPotential('docx')}
                            disabled={Boolean(downloadingPotential)}
                        >
                            {t('evaluationResponses.downloadWord')}
                        </Button>
                    </>
                ) : (
                    <>
                        <Button
                            startIcon={<DownloadIcon />}
                            variant="outlined"
                            onClick={handleDownloadPDF}
                            disabled={!hasPdfUrl}
                        >
                            {t('evaluationResponses.downloadPdf')}
                        </Button>

                        <Button
                            startIcon={<DescriptionIcon />}
                            variant="outlined"
                            onClick={handleDownloadWord}
                            disabled={!hasWordUrl}
                        >
                            {t('evaluationResponses.downloadWord')}
                        </Button>
                    </>
                )}

                <Button onClick={onClose}>
                    {t('close')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}